import { axiosSecuredInstance } from "configurations/axiosConfig";
import { PremiumReceivablesReceiptRecord } from "entities/UIModel/PremiumReceivablesReceiptRecord";

// Mirrors Stewart.Shared.Entities.PolicyData.PaginatedAccountingReceivable
// (AccountingReceivable + ReceivableEntityBase + paging columns). PascalCase
// on the wire because Startup.cs sets JsonSerializerOptions.PropertyNamingPolicy
// = null. Open-ended via the index signature since ReceivableEntityBase/
// AccountingReceivable carry more fields than the UI currently reads.
export type AccountingReceivableDto = {
  AccountingReceivableID: number;
  UnderwriterTypeCode: string | null;
  UnderwriterTypeName: string | null;
  AgentID: string | null;
  AgentName: string | null;
  AccountingPeriod: string | null;
  AccountingYear: string | null;
  AccountingMonth: string | null;
  TransactionYear: string | null;
  TransactionMonth: string | null;
  TransactionDay: string | null;
  TransactionYearMonthDay: string | null;
  GeneralLedgerNumber: string | null;
  GLPRINumber: string | null;
  GLSubNumber: string | null;
  GLDeptNumber: string | null;
  ReceiptTypeCode: string | null;
  ReceiptTypeName: string | null;
  ReceiptNumber: string | null;
  ReceiptAmount: number | null;
  ReceiptDate: string | null;
  TransactionDate: string | null;
  ReelNumber: string | null;
  BankAccountNumber: string | null;
  DataModifier: string | null;
  IsCancel: boolean | null;
  IsError: boolean | null;
  Comments: string | null;
  // Paging (duplicated onto every row by the stored proc, same convention as
  // PolicyTransactionDto).
  RowsPerPage: number | null;
  CurrentRow: number | null;
  TotalRows: number | null;
  CurrentPage: number | null;
  TotalPages: number | null;
  // Query-wide aggregates over the full matching set (ignores pagination,
  // but not chip/column filters — those still apply client-side); same
  // value on every row. Not currently read by the UI — the stats bar
  // computes its own totals over the client-filtered view instead, which is
  // the more useful number for a per-view summary — but declared here since
  // they're on the wire.
  TotalDebits: number | null;
  TotalCredits: number | null;
  TotalNet: number | null;
  TotalDistinctAgents: number | null;
} & Record<string, unknown>;

// Mirrors Stewart.Shared.Entities.PolicyData.AccountingReceivableCriteria.
// Open-ended: arbitrary AccountingReceivable/ReceivableEntityBase field names
// (AgentID, ReceiptTypeCode, etc.) flow through via the index signature.
export type AccountingReceivableCriteriaDto = {
  UserTimeZone: string;
  AccountingPeriod?: string;
  ReceiptDateFrom?: string;
  ReceiptDateTo?: string;
  SortColumn?: string;
  ListCount?: number;
  RequestedPage?: number;
} & Record<string, unknown>;

export const getAccountingReceivableListByCriteria = async (
  criteria: AccountingReceivableCriteriaDto
): Promise<AccountingReceivableDto[]> => {
  try {
    const { data } = await axiosSecuredInstance.post<AccountingReceivableDto[]>(
      "/AccountingReceivable/GetAccountingReceivableListByCriteria",
      criteria
    );
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error("getAccountingReceivableListByCriteria failed", error);
    return [];
  }
};

// yyyy-MM-dd, not MM/dd/yyyy: sorts correctly as plain text across month/year
// boundaries. arColumns' "Trans Date" column formats this for display.
function toSortableDate(value: string | null): string {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mm}-${dd}`;
}

export const mapAccountingReceivableDtoToReceiptRecord = (
  dto: AccountingReceivableDto
): PremiumReceivablesReceiptRecord => ({
  uniqueIdentifier: String(dto.AccountingReceivableID),
  AgentID: dto.AgentID ?? "",
  AgentName: dto.AgentName ?? "",
  AccountingPeriod: dto.AccountingPeriod ?? "",
  ReceiptDate: toSortableDate(dto.TransactionDate),
  ReceiptTypeCode: dto.ReceiptTypeCode ?? "",
  ReceiptNumber: dto.ReceiptNumber ?? "",
  ReceiptAmount: dto.ReceiptAmount ?? 0,
  GLPRINumber: dto.GLPRINumber ?? "",
  GLSubNumber: dto.GLSubNumber ?? "",
  GLDeptNumber: dto.GLDeptNumber ?? "",
  BankAccountNumber: dto.BankAccountNumber ?? "",
  IsCancel: Boolean(dto.IsCancel),
  Comments: dto.Comments ?? "",
});

export const addAccountingReceivable = async (
  entity: Partial<AccountingReceivableDto>,
): Promise<boolean> => {
  try {
    await axiosSecuredInstance.post(
      "/AccountingReceivable/SaveAccountingReceivable",
      entity,
    );
    return true;
  } catch (e) {
    console.error("addAccountingReceivable failed", e);
    return false;
  }
};
